/**
 * FleetKPIWidget — Gesamtflotten-KPIs: Anzahl aktiv, Ø Alter, HU-Fälligkeiten,
 * Laufzeitenden.
 * Issue #1017: Dashboard-Widget: Poolfahrzeug-Status + KPIs Gesamtflotte
 */

import { useEffect, useState } from 'react';
import { Car, Clock, AlertTriangle, RefreshCw } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import {
  berechneFleetKPIs,
  type FlotteFahrzeugInfo,
  type FleetKPIs,
} from '@/lib/fahrzeuge-dashboard-widgets';

const KOOP_RED = '#C41E3A';

interface KPIKachelProps {
  label: string;
  wert: string | number;
  icon: React.ReactNode;
  hinweis?: string;
  warnung?: boolean;
}

function KPIKachel({ label, wert, icon, hinweis, warnung }: KPIKachelProps) {
  return (
    <div
      className={`rounded-xl border px-4 py-3 flex flex-col gap-1.5 transition-colors ${
        warnung ? 'border-amber-200 bg-amber-50/40' : 'border-gray-100 bg-white'
      }`}
      data-testid={`fleet-kpi-${label.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="flex items-center gap-2">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: warnung ? 'rgba(217,119,6,0.1)' : 'rgba(196,30,58,0.06)' }}
        >
          <span className={warnung ? 'text-amber-600' : ''} style={warnung ? undefined : { color: KOOP_RED }}>
            {icon}
          </span>
        </div>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-tight">
          {label}
        </p>
      </div>
      <p className="text-2xl font-bold text-gray-900 tracking-tight pl-0.5">{wert}</p>
      {hinweis && (
        <p className="text-[10px] text-gray-400 pl-0.5">{hinweis}</p>
      )}
    </div>
  );
}

function useFleetKPIs() {
  const [kpis, setKpis] = useState<FleetKPIs | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data, error } = await supabase
          .from('fahrzeuge')
          .select('id, status, anschaffungsdatum, hu_naechste, laufzeitende');

        if (error || !data || cancelled) return;

        const heute = new Date();
        const result = berechneFleetKPIs(data as FlotteFahrzeugInfo[], heute);
        setKpis(result);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  return { kpis, loading };
}

export default function FleetKPIWidget() {
  const { kpis, loading } = useFleetKPIs();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6" data-testid="fleet-kpi-loading">
        <RefreshCw className="w-4 h-4 animate-spin text-gray-300" />
      </div>
    );
  }

  if (!kpis) {
    return (
      <div className="py-5 text-center" data-testid="fleet-kpi-empty">
        <Car className="w-7 h-7 text-gray-200 mx-auto mb-1.5" />
        <p className="text-xs text-gray-400">Keine Fahrzeugdaten</p>
      </div>
    );
  }

  const alterLabel =
    kpis.durchschnittsalterJahre > 0
      ? `${kpis.durchschnittsalterJahre.toFixed(1)} Jahre`
      : '–';

  return (
    <div
      className="grid grid-cols-2 gap-3"
      data-testid="fleet-kpi-widget"
    >
      <KPIKachel
        label="Aktive Fahrzeuge"
        wert={kpis.anzahlAktiv}
        icon={<Car className="w-3.5 h-3.5" />}
        hinweis="Gesamtflotte"
      />
      <KPIKachel
        label="Ø Flottenalter"
        wert={alterLabel}
        icon={<Clock className="w-3.5 h-3.5" />}
        hinweis="Ø aller aktiven Fzg."
      />
      <KPIKachel
        label="HU-Fällig 90 Tage"
        wert={kpis.huFaellig90Tage}
        icon={<AlertTriangle className="w-3.5 h-3.5" />}
        hinweis="Hauptuntersuchung"
        warnung={kpis.huFaellig90Tage > 0}
      />
      <KPIKachel
        label="Laufzeitende 120 Tage"
        wert={kpis.laufzeitendeFaellig120Tage}
        icon={<AlertTriangle className="w-3.5 h-3.5" />}
        hinweis="Finanz-/Leasingende"
        warnung={kpis.laufzeitendeFaellig120Tage > 0}
      />
    </div>
  );
}
